'use client';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Check, Sparkles, Rocket, Zap, Building2 } from 'lucide-react';
import Link from 'next/link';
import { PaymentButton } from '@/components/payment/PaymentButton';
import { motion } from 'framer-motion';
import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import { fetchSubscription } from '@/actions/processPayment.actions';
import { LoadingSpinner } from '@/components/loading-spinner';
import clsx from 'clsx';

//DONE:  If user is logged in, and has a subscription,
/**
 * DONE: If user is logged in, and has a subscription,
 * show the user the current bought plan and the expiry date.
 * Disable the payment button and show the current plan details.
 *
 * */

interface SubscriptionDetails {
  plan: string;
  expiresOn: string;
}

type Feature = string | { text: string; badge: string } | { text: string; icon: React.ReactNode };

const tiers: {
  name: string;
  defaultName: string;
  price: string;
  description: string;
  features: Feature[];
  cta: string;
  href?: string;
  plan?: string;
  isPayment: boolean;
  isPopular?: boolean;
}[] = [
  {
    name: 'SparkStart 🌟',
    defaultName: 'Free',
    price: '₹0',
    description: 'For early-stage job seekers — full of hope, energy, and a bit of uncertainty.',
    features: [
      'Aggregated job listings from top-tier job portals',
      '3 job aligned GEN-AI resume creation/month',
      '5 keyword suggestions',
      '5 AI modifications per resume',
      'Standard job alerts',

    ],
    cta: 'Get Started',
    href: '/signup',
    isPayment: false,
  },
  {
    name: 'Momentum Drive ✈️',
    defaultName: 'Pro',
    price: '₹149',
    description: 'For active seekers — grinding, optimizing, putting in effort daily.',
    features: [
      'Application Tracker',
      'Interview prep templates',
      '20 job aligned GEN-AI resumes',
      '50 AI modifications per resume',
      'Priority job alerts (+30% faster)',
      'Advanced keyword & ATS compatibility reports',
      // {
      //   text: 'Real-time startup funding updates',
      //   icon: <Zap className="h-4 w-4 flex-shrink-0 text-red-500 dark:text-red-400" />,
      // },
      'Aggregated real-time job listings from top-tier job portals',
    ],
    cta: 'Upgrade to Pro',
    plan: 'PRO',
    isPayment: true,
    isPopular: true,
  },
  {
    name: 'Career Catalyst 🚀',
    defaultName: 'Premium',
    price: '₹399',
    description: 'For job seekers ready to shine — polished, ambitious, standout.',
    features: [
      'Everything in Pro',
      'Custom resume templates',
      'LinkedIn profile optimization',
      'AI-powered cover letter generator',
      {
        text: 'Recruiter insights dashboard',
        badge: 'Coming Soon',
      },
      'Curated job openings', // ADHOC: list of jobs based on the match score using AI  
      'Get curated job openings based on your query, delivered when you choose', // Saved queries i.e "Software Developer" run this query every 24 hrs
    ],
    cta: 'Upgrade to Premium',
    plan: 'PREMIUM',
    isPayment: true,
  },
  // {
  //   name: 'Enterprise',
  //   defaultName: 'Enterprise',
  //   price: 'Custom',
  //   description: 'For organizations',
  //   features: [
  //     'Priority support',
  //     // "SSO/SAML",
  //     'Salary Negotiation Toolkit',
  //     'Application Tracker',
  //     'ATS Integration',
  //     'Team collaboration (3 seats)',
  //     {
  //       text: 'One-Click Job Applications',
  //       badge: 'Coming Soon',
  //     },
  //     'Networking Recommendations',
  //     'Bulk resume generation',
  //     'API access',
  //     'Custom integrations',
  //   ],
  //   cta: 'Contact Sales',
  //   href: '/contact',
  //   isPayment: false,
  // },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

const buttonVariants = {
  hover: {
    scale: 1.02,
    translateY: -2,
    transition: {
      duration: 0.2,
      ease: 'easeInOut',
    },
  },
  tap: {
    scale: 0.98,
    translateY: 1,
  },
};

const borderAnimations = {
  free: {
    initial: {
      boxShadow: '0 0 0 rgba(16, 185, 129, 0)',
    },
    hover: {
      boxShadow: '0 0 20px 5px rgba(16, 185, 129, 0.2)',
      transition: {
        duration: 1,
        repeat: Infinity,
        repeatType: 'mirror',
      },
    },
  },
  pro: {
    initial: {
      boxShadow: '0 0 0 rgba(239, 68, 68, 0)',
    },
    hover: {
      boxShadow: '0 0 30px 10px rgba(239, 68, 68, 0.3)',
      transition: {
        duration: 1.5,
        repeat: Infinity,
        repeatType: 'mirror',
      },
    },
  },
  premium: {
    initial: {
      boxShadow: '0 0 0 rgba(139, 92, 246, 0)',
    },
    hover: {
      boxShadow: '0 0 25px 8px rgba(139, 92, 246, 0.25)',
      transition: {
        duration: 1.2,
        repeat: Infinity,
        repeatType: 'mirror',
      },
    },
  },
  enterprise: {
    initial: {
      boxShadow: '0 0 0 rgba(156, 163, 175, 0)',
    },
    hover: {
      boxShadow: '0 0 20px 5px rgba(156, 163, 175, 0.2)',
      transition: {
        duration: 1.3,
        repeat: Infinity,
        repeatType: 'mirror',
      },
    },
  },
} as const;

export default function PricingPage({ heroSection = false }: { heroSection: boolean }) {
  const { data: session, status } = useSession();
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(
    null
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkSubscription = async () => {
      if (status === 'authenticated' && session?.user?.id) {
        try {
          const subscribedTo = await fetchSubscription(session.user.id);
          setSubscription(subscribedTo);
        } catch (error) {
          console.error('Error fetching subscription:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    checkSubscription();
  }, [session?.user?.id, status]);

  if (loading) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getGridCols = () => {
    if (tiers.length === 1) return 'grid-cols-1';
    if (tiers.length === 2) return 'sm:grid-cols-2';
    if (tiers.length === 3) return 'sm:grid-cols-2 lg:grid-cols-3';
    if (tiers.length === 4) return 'sm:grid-cols-2 lg:grid-cols-4';
    if (tiers.length === 5) return 'sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5';
    return 'sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4'; // fallback
  };

  const getGridMaxWidth = () => {
    if (tiers.length <= 3) return 'max-w-4xl';
    if (tiers.length === 4) return 'max-w-7xl';
    if (tiers.length >= 5) return 'max-w-full';
    return 'max-w-4xl';
  };

  return (
    <div id='pricing' className="w-full min-h-fit dark:bg-gray-900/40 bg-gray-50/50 md:px-16 px-5 flex flex-col items-center py-16 md:py-24">
      <motion.div
        className="mx-auto max-w-7xl px-4 lg:px-6 pb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {subscription?.plan && heroSection === false && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-8 p-4 rounded-lg border ${
              subscription.plan === 'PRO'
                ? 'bg-gradient-to-r from-red-500 to-pink-500 dark:from-red-500/20 dark:to-pink-500/20 border-red-200 dark:border-red-800'
                : 'bg-gradient-to-r from-violet-500 to-purple-500 dark:from-violet-500/20 dark:to-purple-500/20 border-violet-200 dark:border-violet-800'
            }`}
          >
           {heroSection === false && ( <div className="flex items-center justify-between">
              <div>
                <h3
                  className={`text-lg font-semibold ${
                    subscription.plan === 'PRO'
                      ? 'text-white dark:text-white'
                      : 'text-white dark:text-white'
                  }`}
                >
                  Current Plan: {subscription.plan}
                </h3>
                <p
                  className={`text-sm ${
                    subscription.plan === 'PRO'
                      ? 'text-white/90 dark:text-white/90'
                      : 'text-white/90 dark:text-white/90'
                  }`}
                >
                  Expires on: {formatDate(subscription.expiresOn)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles
                  className={`h-5 w-5 ${
                    subscription.plan === 'PRO'
                      ? 'text-white dark:text-white'
                      : 'text-white dark:text-white'
                  }`}
                />
                <span
                  className={`text-sm font-medium ${
                    subscription.plan === 'PRO'
                      ? 'text-white dark:text-white'
                      : 'text-white dark:text-white'
                  }`}
                >
                  Active Subscription
                </span>
              </div>
            </div>)}
          </motion.div>
        )}
        <motion.div
          className="mx-auto max-w-4xl text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-base font-semibold leading-7 text-indigo-500 dark:text-indigo-400">
            Pricing
          </h2>
          <p className="mt-1 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
            Choose the right plan for&nbsp;you
          </p>
          <p className="mt-4 text-base leading-7 text-gray-600 dark:text-gray-300">
            Select the perfect plan to accelerate your job search and career
            growth
          </p>
        </motion.div>
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className={clsx(
            'isolate mx-auto mt-8 grid gap-y-6 sm:mt-12 lg:mx-0',
            getGridCols(),
            getGridMaxWidth(),
            'gap-x-4 xl:gap-x-6 justify-center'
          )}
        >
          {tiers.map((tier) => (
            <motion.div
              key={tier.defaultName}
              variants={item}
              whileHover={{ scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <Card
                className={`relative flex h-full flex-col justify-between backdrop-blur-sm bg-white/80 dark:bg-gray-800/90 transform-gpu overflow-hidden border-2 border-gray-200 dark:border-gray-700 transition-colors duration-300 ${
                  tier.isPopular
                    ? 'hover:border-red-500 dark:hover:border-red-400'
                    : tier.defaultName === 'Premium'
                      ? 'hover:border-violet-500 dark:hover:border-violet-400'
                      : tier.defaultName === 'Enterprise'
                        ? 'hover:border-gray-400 dark:hover:border-gray-500'
                        : 'hover:border-emerald-500 dark:hover:border-emerald-400'
                }`}
              >
                <motion.div
                  className="absolute inset-0 rounded-lg opacity-0 transition-opacity duration-300"
                  initial="initial"
                  whileHover={{
                    opacity: 1,
                    transition: { duration: 0.3 },
                  }}
                  variants={
                    tier.defaultName === 'Free'
                      ? borderAnimations.free
                      : tier.defaultName === 'Pro'
                        ? borderAnimations.pro
                        : tier.defaultName === 'Premium'
                          ? borderAnimations.premium
                          : borderAnimations.enterprise
                  }
                />
                <CardHeader className="space-y-1 p-4">
                  <div className="flex items-center justify-between">
                    <CardTitle
                      className={`text-lg font-bold ${
                        tier.isPopular
                          ? 'text-red-500 dark:text-red-400'
                          : tier.defaultName === 'Premium'
                            ? 'text-violet-500 dark:text-violet-400'
                            : tier.defaultName === 'Enterprise'
                              ? 'text-gray-700 dark:text-gray-300'
                              : 'text-emerald-600 dark:text-emerald-400'
                      }`}
                    >
                      {tier.name}
                    </CardTitle>
                 
                  </div>
                  <CardDescription className="text-sm text-gray-600 dark:text-gray-300">
                    {tier.description}
                  </CardDescription>
                  <div className="mt-2 flex items-baseline">
                    <span
                      className={`text-3xl font-bold tracking-tight ${
                        tier.isPopular
                          ? 'text-red-500 dark:text-red-400'
                          : tier.defaultName === 'Premium'
                            ? 'text-violet-500 dark:text-violet-400'
                            : tier.defaultName === 'Enterprise'
                              ? 'text-gray-700 dark:text-gray-300'
                              : 'text-emerald-600 dark:text-emerald-400'
                      }`}
                    >
                      {tier.price}
                    </span>
                    {tier.price !== 'Custom' && (
                      <span className="ml-1 text-sm font-semibold leading-6 text-gray-500 dark:text-gray-400">
                        /month
                      </span>
                    )}
                  </div>
                  
                </CardHeader>
                <CardContent className="flex-1 p-4 pt-0">
                  <ul className="space-y-3">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check className="h-4 w-4 flex-shrink-0 text-indigo-500 dark:text-indigo-400" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 min-w-0 flex-1">
                          {typeof feature === 'string' ? (
                            feature
                          ) : 'badge' in feature ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="min-w-0">{feature.text}</span>
                              <span className="inline-flex items-center rounded-full bg-yellow-100 dark:bg-yellow-900/50 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:text-yellow-300 whitespace-nowrap">
                                {feature.badge}
                              </span>
                            </div>
                          ) : (
                            <span className="min-w-0 flex items-center gap-1">
                              {feature.text}
                              {feature.icon}
                            </span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="p-4 pt-0">
                  {tier.isPayment ? (
                    <motion.div
                      className="w-full relative group"
                      variants={buttonVariants}
                      whileHover="hover"
                      whileTap="tap"
                    >
                      {subscription && subscription.plan === tier.plan ? (
                        <div
                          className={`w-full py-2.5 px-4 rounded-lg border ${
                            tier.isPopular
                              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                              : 'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800'
                          }`}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <Check
                              className={`h-4 w-4 ${
                                tier.isPopular
                                  ? 'text-red-500 dark:text-red-400'
                                  : 'text-violet-500 dark:text-violet-400'
                              }`}
                            />
                            <span
                              className={`text-sm font-medium ${
                                tier.isPopular
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-violet-600 dark:text-violet-400'
                              }`}
                            >
                              Current Plan
                            </span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div
                            className={`absolute -inset-1 rounded-lg bg-gradient-to-r ${
                              tier.isPopular
                                ? 'from-red-500 via-pink-500 to-purple-500 dark:from-red-400 dark:via-pink-400 dark:to-purple-400'
                                : 'from-violet-500 to-purple-500 dark:from-violet-400 dark:to-purple-400'
                            } opacity-40 blur-lg group-hover:opacity-75 transition duration-1000 group-hover:duration-200`}
                          />
                          <PaymentButton
                            plan={tier.plan as 'PRO' | 'PREMIUM'}
                            className={`relative w-full font-semibold transform-gpu transition-all duration-300 rounded-lg flex items-center justify-center gap-2 ${
                              tier.isPopular
                                ? 'bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 dark:from-red-500 dark:to-pink-500 dark:hover:from-red-400 dark:hover:to-pink-400 text-white shadow-[0_8px_16px_-3px_rgba(239,68,68,0.4)] hover:shadow-[0_12px_20px_-3px_rgba(239,68,68,0.5)] dark:shadow-[0_8px_16px_-3px_rgba(239,68,68,0.4)] dark:hover:shadow-[0_12px_20px_-3px_rgba(239,68,68,0.5)] border border-red-400/30'
                                : 'bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 dark:from-violet-500 dark:to-purple-500 dark:hover:from-violet-400 dark:hover:to-purple-400 text-white shadow-[0_8px_16px_-3px_rgba(139,92,246,0.4)] hover:shadow-[0_12px_20px_-3px_rgba(139,92,246,0.5)] dark:shadow-[0_8px_16px_-3px_rgba(139,92,246,0.4)] dark:hover:shadow-[0_12px_20px_-3px_rgba(139,92,246,0.5)] border border-violet-400/30'
                            }`}
                            icon={
                              tier.isPopular ? (
                                <Sparkles className="h-4 w-4 text-yellow-200 animate-pulse" />
                              ) : (
                                <Zap className="h-4 w-4 text-violet-200" />
                              )
                            }
                          />
                        </>
                      )}
                    </motion.div>
                  ) : (
                    <motion.div
                      className="w-full relative group"
                      variants={buttonVariants}
                      whileHover="hover"
                      whileTap="tap"
                    >
                      <div
                        className={`absolute -inset-1 rounded-lg bg-gradient-to-r ${
                          tier.defaultName === 'Enterprise'
                            ? 'from-gray-500 to-gray-700 dark:from-gray-600 dark:to-gray-800'
                            : 'from-emerald-500 to-teal-500 dark:from-emerald-400 dark:to-teal-400'
                        } opacity-40 blur-lg group-hover:opacity-75 transition duration-1000 group-hover:duration-200`}
                      />
                      <Button
                        asChild
                        className={`relative w-full font-semibold transform-gpu transition-all duration-300 rounded-lg flex items-center justify-center gap-2 ${
                          tier.defaultName === 'Enterprise'
                            ? 'bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-800 hover:to-gray-950 dark:from-gray-600 dark:to-gray-800 dark:hover:from-gray-700 dark:hover:to-gray-900 text-white shadow-[0_8px_16px_-3px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_20px_-3px_rgba(0,0,0,0.4)] border border-gray-600/30'
                            : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 dark:from-emerald-500 dark:to-teal-500 dark:hover:from-emerald-400 dark:hover:to-teal-400 text-white shadow-[0_8px_16px_-3px_rgba(16,185,129,0.4)] hover:shadow-[0_12px_20px_-3px_rgba(16,185,129,0.5)] dark:shadow-[0_8px_16px_-3px_rgba(5,150,105,0.4)] dark:hover:shadow-[0_12px_20px_-3px_rgba(5,150,105,0.5)] border border-emerald-400/30'
                        }`}
                      >
                        <Link
                          href={tier.href || '/'}
                          className="flex items-center gap-2"
                        >
                          {tier.defaultName === 'Enterprise' ? (
                            <Building2 className="h-4 w-4 text-gray-200" />
                          ) : (
                            <Rocket className="h-4 w-4 text-emerald-200" />
                          )}
                          {tier.cta}
                        </Link>
                      </Button>
                    </motion.div>
                  )}
                </CardFooter>
                {tier.isPopular && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                      >
                        <motion.div
                          className="relative bg-gradient-to-r from-red-600 to-red-700 px-3 py-1.5 shadow-lg overflow-hidden"
                          whileHover={{ scale: 1.05 }}
                          transition={{
                            type: 'spring',
                            stiffness: 400,
                            damping: 10,
                          }}
                        >
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-t from-red-500/50 via-orange-500/30 to-yellow-500/20"
                            animate={{
                              backgroundPosition: ['0% 0%', '0% 100%', '0% 0%'],
                            }}
                            transition={{
                              duration: 3,
                              repeat: Infinity,
                              ease: 'easeInOut',
                            }}
                          />
                          <div className="relative flex items-center justify-center gap-2 text-xs font-medium">
                            <motion.div
                              animate={{
                                scale: [1, 1.2, 1],
                                rotate: [0, -3, 3, 0],
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                repeatType: 'mirror',
                              }}
                              className="relative"
                            >
                              <motion.div
                                className="absolute inset-0 blur-sm bg-orange-400/50"
                                animate={{
                                  opacity: [0.5, 0.8, 0.5],
                                }}
                                transition={{
                                  duration: 1.5,
                                  repeat: Infinity,
                                  repeatType: 'mirror',
                                }}
                              />
                            🔥
                            </motion.div>
                            <span className="text-white font-bold tracking-wide relative z-10">
                              Most Popular
                            </span>
                          </div>
                        </motion.div>
                      </motion.div>
                    )}
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
}
